import React, { useState, useEffect, useContext, useRef } from "react";
import { Grommet, Box, TextInput, Text, Meter, Stack, Header, Pagination, Spinner, Select } from "grommet";
import { useFetch } from "use-http";
import { UserContext } from "../../App";
import { getDBURL, isValidYearString, usePopupFocus, } from "../Utils/Utils";
import { useErrorLogger } from '../Base components/Logger';
import ExcelExport  from "../Base components/ExcelExport";
import EvaluationSummary from "../Base components/EvaluationSummary";                     
import { getLabelByIndex } from "../Utils/RBGUtils"; // Import your utility function

import "./EvaluetionsByStationsReport.css"; // Import custom CSS

const customTheme = {
    global: {
        colors: {
            brand: "#7b7bd3",
            text: "#333", // Dark gray for readability
            // border: "#ccc", // Lighter gray for borders
            border: "#7b7bd3",
            placeholder: "#d7d7fc", 
        },
        focus: {
            outline: {
                color: "#d7d7fc",
            },
        },          
        font: {
            family: 'Roboto, sans-serif', // Choose a font you like
        },
        dataTable: {
            cell: {
                padding: { vertical: 'xsmall', horizontal: 'small' },
            },
            row: {
                background: 'alternate', // Uses a color from the theme
            },
        },
    },
    meter: {
        color: "#d7d7fc"
    },  
}

const EvaluetionsByStationsReport = () => {
    const currentUser = useContext(UserContext);
    const { get, loading } = useFetch(getDBURL());
    const { DBlogError } = useErrorLogger();

    const [stationsList, setStationsList] = useState([]);
    const [columns, setColumns] = useState([]);
    const [chosenYear, setChosenYear] = useState(new Date().getFullYear());
    const [newVal, setNewVal] = useState(new Date().getFullYear());
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50);
    const [chosenGrade, setChosenGrade] = useState("All");
    const [filteredStationsList, setFilteredStationsList] = useState([]);
    const [stationNameFilter, setStationNameFilter] = useState('');
    
    const [selectedEvaluation, setSelectedEvaluation] = useState(null);
    const [showEvaluationSummary, setShowEvaluationSummary] = useState(false);

    // Manage focus when opening modal windows
    usePopupFocus(showEvaluationSummary, "eval-summ");

    const [evIdFilter, setEvIdFilter] = useState('All (Include Not Evaluated Yet)');

    const gradeOptions = ["All", "Commercial", "Second Chance", "Eliminate"];
    const evIdOptions = ["All (Include Not Evaluated Yet)", "Evaluated on Selected Year"];

    // Keys to exclude from columns and content
    const excludedKeys = ["EvId", "uniqueKey", "rowIndex", "Seeds", "Img"];

    // for getting value by index in RBGUtils.
    const keysToProcessWithLabel = [
        'Pruning',
        'Fertility',
        'ColorUniformity',
        'ShotBerries',
        'WaterBerries',
        'BunchDensity',
        'Rudiment',
        'OverallAppeal',
        'BerryTexture',
        'Skin',
        'Stem',
        'BerrySize',
        'AcidAtEval',
        'Astringency',
        'EatingQuality',
        'CapStem',
        'Attachment',
        'Flavor',
        'SkinStructure',
        'Brush',        
    ];
    
    const nameOptionMap = {
        TSS_At_Eval: 'TSS at Evaluation',
        TSS_At_Ripeness: 'TSS at Ripeness',
        PH_At_Ripeness: 'pH at Ripeness',
        TA_At_Ripeness: 'TA at Ripeness',
        AcidAtEval: 'Acidity at Evaluation'
    };

    useEffect(() => {
        const fetchEvaluations = async () => {
            try {
                const timestamp = Date.now();
                const response = await get(`/loadAllEvaluationsForAllStations/year=${chosenYear}?dt=${timestamp}`);
                if (!response.success) {
                    throw new Error(`${response.message}`);
                }
                const data = response.result.map((item, index) => ({
                    ...item,
                    uniqueKey: `${item.StationName}-${item.StartColumn}-${index}`,
                    rowIndex: index + 1
                }));
                if (data !== undefined && data.length > 0) {
                    setStationsList(data);
                    setFilteredStationsList(data); // Initially, show all data
                    generateColumns(data[0]);
                }
            } catch (error) {
                console.error('🔴 Error in loadAllEvaluationsForAllStations >>', error.message);
                setError("An error occurred while fetching data. Please try again later.");
                const errorDetails = {
                    error: error,
                    parameters: { year: chosenYear },
                    userName: currentUser.email,
                    moduleName: 'EvaluetionsByStationsReport',
                    functionName: 'fetchEvaluations',
                };
                await DBlogError(errorDetails);
            }
        };
        fetchEvaluations();
    }, [chosenYear]);

    useEffect(() => {
        filterStationsData();
    }, [stationsList, chosenGrade, stationNameFilter, evIdFilter]);

    const generateColumns = (sampleData) => {
        const dynamicColumns = [
            {
                property: "rowIndex",
                header: "No.",
                render: (datum) => (
                    <Box pad={{ vertical: "xsmall" }}>
                        <Text size="small">{datum.rowIndex}</Text>
                    </Box>
                ),
            },
            ...Object.keys(sampleData).map((key) => {
                // Convert camelCase or other cases to space-separated words
                let headerLabel = nameOptionMap[key] || key.split(/(?=[A-Z])/).join(" ");

                // Check if headerLabel should be converted to single-word form
                // Preserve original key if it exists in `nameOptionMap`
                let formattedKey = nameOptionMap[key] ? key : headerLabel.replace(/\s+/g, '');

                if (key === "EvaluationProgress") {
                    return {
                        property: key,
                        header: "Progress",
                        render: (datum) => (
                            datum.EvaluationProgress === "" ? (
                                <Box pad={{ vertical: 'xsmall' }} width="xxsmall">
                                    <Text size="xsmall"> </Text>
                                </Box>
                            ) : (
                                <Box pad={{ vertical: 'xsmall' }} width="xsmall">
                                    <Stack anchor="center">
                                        <Meter
                                            values={[{ value: Math.round(datum.EvaluationProgress) }]}
                                            thickness="small"
                                            background="light-2"
                                            // type="semicircle"
                                            value="red"
                                        />
                                        <Box align="center" justify="center" fill>
                                            <Text size="xsmall">{Math.round(datum.EvaluationProgress)}%</Text>
                                        </Box>
                                    </Stack>
                                </Box>
                            )
                        ),
                    };
                } else if (key === "Notes") {
                    return {
                        property: key,
                        header: "Notes",
                        render: (datum) => (
                            <Box pad={{ vertical: "xsmall" }} style={{ width: '250px' }}>
                                <Text size="small">{datum[key] || ''}</Text>
                            </Box>
                        ),
                    };
                } else if (key === "uniqueKey" || key === "rowIndex" || key === "EvId") {
                    return null;
                } else {
                    return {
                        property: key,
                        header: headerLabel,
                        render: (datum) => {
                            let value = datum[key];
                            if (keysToProcessWithLabel.includes(formattedKey)) {
                                value = getLabelByIndex(formattedKey, value);
                            }
                            value = String(value || '').replace(/^,\s*/, '');
                            return (
                                <Box pad={{ vertical: "xsmall" }}>
                                    <Text size="small">{value}</Text>
                                </Box>
                            );
                        },
                    };
                }
            }).filter(column => column !== null)
        ];
        setColumns(dynamicColumns);
    };

    

    const setSearchYear = (newYearValue) => {
        if (newYearValue !== newVal && error !== '')
            setError('');
        setNewVal(newYearValue);
        const isValid = isValidYearString(newYearValue, false);
        if (!isValid.res) {
            setError(isValid.msg);
            return;
        }
        setChosenYear(newYearValue);
    }

    const getGradeRange = (grade) => {
        if (grade < 35) {
            return "Eliminate";
        }
        if (grade < 80) {
            return "Second Chance";
        }
        return "Commercial";
    }

    const textColor = (grade) => {
        if (grade === '' || grade === undefined) return "black";
        if (grade < 35) return "#C41E3A";
        if (grade < 80) return "#ed9e15";
        return "#088F8F";
    };

    const filterStationsData = () => {
        let filteredList = stationsList.filter((queryData) => {
            const grade = parseInt(queryData.Grade, 10);
            const gradeRange = !isNaN(grade) ? getGradeRange(grade) : "";
            const matchesGrade = chosenGrade === "All" || gradeRange === chosenGrade;
            const stationNameLowerCase = queryData.StationName?.toLowerCase(); // Ensure StationName is not null before using toLowerCase()
            const stationNameFilterLowerCase = stationNameFilter.toLowerCase(); // Convert filter input to lower case

            // Check if stationNameFilter is empty or matches StationName ignoring case
            const matchesStationName = !stationNameFilter || (stationNameLowerCase && stationNameLowerCase.includes(stationNameFilterLowerCase));

            // Filter by EvId based on the selected option
            const matchesEvId = evIdFilter === "All (Include Not Evaluated Yet)" ||
                (evIdFilter === "Evaluated on Selected Year" && queryData.EvId !== null);

            return matchesGrade && matchesStationName && matchesEvId;
        });

        setFilteredStationsList(filteredList);
        setCurrentPage(1); // Always reset to the first page when filters change
    };

    

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredStationsList.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = ({ startIndex }) => {
        setCurrentPage(Math.ceil(startIndex / itemsPerPage) + 1);
    };

    return (
        <Grommet theme={customTheme}> 
            <Box fill>
                <Header background="brand" pad="small" height="xxsmall" justify="center">
                    <Text alignSelf="center" textAlign="center" color="white" size="large" weight="bold">
                        Evaluations By Stations Report
                    </Text>
                </Header>
    
                <Box direction="row-responsive" pad={{ horizontal: "medium", vertical: "small" }} align="center" justify="between">
                    <Box className="filter-container"  textColor="brand">
                        <Text style={{ whiteSpace: 'nowrap' }}>Year of evaluation:</Text>
                        <TextInput
                            type="text"
                            value={newVal}
                            size="medium"
                            style={{ padding: "5px 0 5px 10px" }}
                            onChange={(event) => setSearchYear(event.target.value)}
                        />
                    </Box>
                    <Box className="filter-container">
                        <Text style={{ whiteSpace: 'nowrap' }}>Evaluation Status:</Text>
                        <Select
                            style={{ padding: "5px 0 5px 10px" }}
                            options={evIdOptions}
                            value={evIdFilter}
                            onChange={({ option }) => {
                                setEvIdFilter(option);
                            }}
                        />
                    </Box>
                    <Box className="filter-container">
                        <Text style={{ whiteSpace: 'nowrap' }}>Grade:</Text>
                        <Select
                            style={{ padding: "5px 0 5px 10px" }}
                            options={gradeOptions}
                            value={chosenGrade}
                            onChange={({ option }) => {
                                setChosenGrade(option);
                            }}
                        />
                    </Box>
                    <Box className="filter-container">
                        <Text style={{ whiteSpace: 'nowrap' }}>Station Name:</Text>
                        <TextInput
                            style={{ padding: "5px 0 5px 10px" }}
                            placeholder="Search by Station Name"
                            value={stationNameFilter}
                            onChange={(event) => setStationNameFilter(event.target.value)}
                        />
                    </Box>
                    <Box className="filter-container">
                        <ExcelExport 
                            data={filteredStationsList} 
                            name='Evaluations'
                            excludedKeys={excludedKeys}
                            keysToProcessWithLabel={keysToProcessWithLabel}>
                        </ExcelExport>
                    </Box>
                </Box>
    
                {loading && (
                    <Box align="center" justify="center" pad="large">
                        <Spinner size="medium" />
                    </Box>
                )}
                {error && (
                    <Box align="center" justify="center" pad="large">
                        <Text size="large" color="status-critical">{error}</Text>
                    </Box>
                )}
                {!loading && !error && (
                    <Box className="table-container" fill>
                        <Box className="table-wrapper" fill>
                            <table style={{ border: '2px solid #7b7bd3' }}>
                                <thead className="data-table-header">
                                    <tr>
                                        {columns.map((column) => (
                                            <th key={column.property}>{column.header}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentItems.length > 0 ? (
                                        currentItems.map((item) => (
                                            <tr key={item.uniqueKey} style={{ color: textColor(item.Grade) }}>
                                                {columns.map((column) => {
                                                    const isNotesOrShape = column.property === "Notes" || column.property.toLowerCase().includes('shape');
                                                    const isEvCreateDate = column.property === "Evaluation Create Date";

                                                    return (
                                                        <td
                                                            key={`${item.uniqueKey}-${column.property}`}
                                                            style={
                                                                isNotesOrShape
                                                                    ? { minWidth: '250px' }
                                                                    : { whiteSpace: 'nowrap' }
                                                            }
                                                        >
                                                            {isEvCreateDate && item.EvId ? (
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedEvaluation(item.EvId);
                                                                        setShowEvaluationSummary(true);
                                                                    }}
                                                                    style={{
                                                                        color: textColor(item.Grade),
                                                                        border: `2px solid ${textColor(item.Grade)}`, // Dynamically set border color
                                                                        background: "transparent", // Optional: ensures a clean button style
                                                                        padding: "5px 10px", // Optional: improves button appearance
                                                                        borderRadius: "4px", // Optional: adds rounded corners
                                                                        cursor: "pointer", // Optional: adds a pointer cursor on hover
                                                                    }}
                                                                >
                                                                    {item[column.property]}
                                                                </button>
                                                            ) : column.render ? (
                                                                column.render(item)
                                                            ) : (
                                                                item[column.property]
                                                            )}
                                                        </td>
                                                    );                                                    
                                                })}

                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={columns.length} style={{ textAlign: 'center' }}>No data available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </Box>
                        {filteredStationsList.length > itemsPerPage && (
                            <Box className="fixed-pagination pagination-container">
                                <Pagination
                                    numberItems={filteredStationsList.length}
                                    step={itemsPerPage}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {showEvaluationSummary && 
            <Box>
                <EvaluationSummary plant={null} station={null} treatment={null} 
                    setShowEvaluationSummary={setShowEvaluationSummary}
                    evaluation={selectedEvaluation} mainColor={customTheme.global.colors.brand}
                />
            </Box> 
            } 


        </Grommet>
    );
    
};

export default EvaluetionsByStationsReport;
